/**
 * Request-body contracts for the routes both servers share (routines,
 * calendar, contacts, knowledge, projects, the operator profile, the agent
 * identity, skills, onboarding), checked by `checkRequest`
 * (`./requestCheck.ts`). The settings routes have theirs in
 * `./settings/routeContracts.ts`. A schema with a hand-written input interface
 * is paired with a compile-time parity check against it, so the two can't
 * drift apart unnoticed.
 *
 *   - Strict objects: a field the contract doesn't name is refused with its
 *     name, never silently dropped — a misspelt field fails loudly instead of
 *     "saving" without effect. Same rule as tool calls (ADR-0004 slice 2).
 *   - Required strings use `.trim().min(1)` (whitespace-only is refused; the
 *     stored value is trimmed).
 *   - `.optional()` yields `T | undefined`, matching the interfaces'
 *     `field?: T | undefined`.
 */
import { z } from 'zod';
import type {
  CreateCalendarInput,
  UpdateCalendarInput,
  CreateContactInput,
  UpdateContactInput,
  CreateRoutineInput,
  UpdateRoutineInput,
} from './types.js';
import {
  CALENDAR_EVENT_TYPES,
  CLAUDE_AUTH_MODES,
  CLAUDE_PERMISSION_MODES,
  CONTACT_TYPES,
  LOG_LEVELS,
  TOOL_CATEGORIES,
} from './types.js';
import { boundedCount, flag, instant, optionalCount, text, wholeNumber } from './queryValues.js';
import { scheduleProblem } from './schedule.js';
import { isKnownTimeZone, isWellFormedLocale } from './intl.js';
import type { OperatorPatch } from './operator.js';
import { labelProblem } from './labels.js';
import { skillNameProblem, skillProblem } from './skills.js';
import { MAX_ATTACHMENTS_PER_MESSAGE } from './chatAttachments.js';

// ---------------------------------------------------------------------------
// Type-parity helper
// ---------------------------------------------------------------------------

/**
 * Compile-time bidirectional assignability check. Both `A extends B` and
 * `B extends A` must hold; otherwise `_Check` narrows to `false` and the
 * `const _x: _Check<...> = true` line below fails to type-check.
 *
 * This is the drift alarm. If someone adds a field to `CreateRoutineInput`
 * without updating `CreateRoutineSchema` (or vice versa), the build breaks
 * loudly here — not silently at request time.
 */
type MutuallyAssignable<A, B> = A extends B ? (B extends A ? true : false) : false;

// ---------------------------------------------------------------------------
// Routines
// ---------------------------------------------------------------------------

/** A field that must hold text: trimmed, and empty is refused. */
const requiredText = z.string().trim().min(1, 'required');
/** Text on one line — it lands in a heading, a frontmatter field or a label. */
const oneLine = (schema: z.ZodString) => schema.refine((v) => !/[\r\n]/.test(v), 'must be one line');
/** A label (`./labels.ts`): one line, at most LABEL_MAX characters. */
const label = (schema: z.ZodString) =>
  schema.superRefine((v, ctx) => {
    const problem = labelProblem(v);
    if (problem) ctx.addIssue({ code: 'custom', message: problem });
  });
/** An IANA time zone this runtime knows. */
const timeZone = z
  .string()
  .trim()
  .refine(isKnownTimeZone, 'unknown time zone (use an IANA name such as America/New_York)');
/** Refuse a schedule the routine engine couldn't run (`../schedule.ts`). */
export function validSchedule(
  value: { cron?: string | undefined; timezone?: string | undefined },
  ctx: z.RefinementCtx,
): void {
  if (value.cron === undefined) return;
  const problem = scheduleProblem(value.cron, value.timezone);
  if (problem) ctx.addIssue({ code: 'custom', path: ['cron'], message: problem });
}

/**
 * A new routine's fields, before the schedule check — the agent tool builds on
 * these (it fills `project` from the conversation), so they're exported with
 * {@link validSchedule} rather than copied.
 */
export const CreateRoutineFields = z.strictObject({
  label: label(requiredText),
  cron: requiredText,
  prompt: requiredText,
  timezone: timeZone.optional(),
  enabled: z.boolean().optional(),
  id: z.string().optional(),
  category: z.string().optional(),
  project: requiredText,
  model: z.string().optional(),
  allowedTools: z.array(requiredText).optional(),
});

export const CreateRoutineSchema = CreateRoutineFields.superRefine(validSchedule);
const _createRoutineParity: MutuallyAssignable<z.infer<typeof CreateRoutineSchema>, CreateRoutineInput> = true;
void _createRoutineParity;

/**
 * A change to a routine. Absent = unchanged. Text that a routine must have
 * (label, schedule, prompt, project) can't be emptied; `""` clears category,
 * `""` or `null` clears model and reviewedAt, `[]` or `null` clears allowedTools.
 */
/** The update fields without the schedule check — for contracts that drop or add fields (the agent tool). */
export const UpdateRoutineFields = z.strictObject({
  label: label(requiredText).optional(),
  cron: requiredText.optional(),
  timezone: timeZone.optional(),
  prompt: requiredText.optional(),
  enabled: z.boolean().optional(),
  category: z.string().optional(),
  project: requiredText.optional(),
  model: z.string().nullable().optional(),
  reviewedAt: z.string().nullable().optional(),
  archiveCandidate: z.boolean().optional(),
  allowedTools: z.array(requiredText).nullable().optional(),
});
export const UpdateRoutineSchema = UpdateRoutineFields.superRefine(validSchedule);
const _updateRoutineParity: MutuallyAssignable<z.infer<typeof UpdateRoutineSchema>, UpdateRoutineInput> = true;
void _updateRoutineParity;

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------

/**
 * A value that starts with a real calendar date, `YYYY-MM-DD` (a time may
 * follow). Events are listed and filtered by this prefix, so a value without
 * one would never show up. Shared by the REST contract and the agent tools.
 */
export const DatePrefixed = z
  .string()
  .trim()
  .refine((value) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!m) return false;
    const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  }, 'must start with a real date, YYYY-MM-DD');

const CalendarEventTypeSchema = z.enum(CALENDAR_EVENT_TYPES);

export const CreateCalendarSchema = z.strictObject({
  title: requiredText,
  eventDate: DatePrefixed,
  description: z.string().optional(),
  eventType: CalendarEventTypeSchema.optional(),
  project: requiredText,
});
const _createCalendarParity: MutuallyAssignable<z.infer<typeof CreateCalendarSchema>, CreateCalendarInput> = true;
void _createCalendarParity;

export const UpdateCalendarSchema = z.strictObject({
  title: requiredText.optional(),
  description: z.string().optional(),
  eventDate: DatePrefixed.optional(),
  eventType: CalendarEventTypeSchema.optional(),
  project: requiredText.optional(),
});
const _updateCalendarParity: MutuallyAssignable<z.infer<typeof UpdateCalendarSchema>, UpdateCalendarInput> = true;
void _updateCalendarParity;

// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------

const ContactTypeSchema = z.enum(CONTACT_TYPES);

export const CreateContactSchema = z.strictObject({
  name: z.string().trim().min(1, 'required'),
  contactType: ContactTypeSchema.optional(),
  title: z.string().optional(),
  emails: z.array(z.string()).optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  company: z.string().optional(),
  location: z.string().optional(),
  website: z.string().optional(),
  projects: z.array(requiredText).min(1, 'a contact belongs to at least one project'),
  tags: z.array(z.string()).optional(),
  linkedContactId: z.string().optional(),
  linkType: z.string().optional(),
  notes: z.string().optional(),
});
const _createContactParity: MutuallyAssignable<z.infer<typeof CreateContactSchema>, CreateContactInput> = true;
void _createContactParity;

export const UpdateContactSchema = CreateContactSchema.partial();
const _updateContactParity: MutuallyAssignable<z.infer<typeof UpdateContactSchema>, UpdateContactInput> = true;
void _updateContactParity;

// ---------------------------------------------------------------------------
// Knowledge entries
// ---------------------------------------------------------------------------

/**
 * A blank `description` means "write one from the content" — the store
 * derives it from the first line (`deriveDescription`), on create and on edit
 * alike, so no entry is ever left without a label.
 */
export const CreateKnowledgeSchema = z.strictObject({
  project: requiredText,
  content: requiredText,
  description: label(z.string().trim()).optional(),
  folder: z.string().trim().optional(),
});

/** A change to an entry: new content, a new label, or both. */
export const UpdateKnowledgeSchema = z
  .strictObject({
    content: requiredText.optional(),
    description: label(z.string().trim()).optional(),
  })
  .refine((v) => v.content !== undefined || v.description !== undefined, 'send content, description, or both');

/** Move an entry to another (existing) project — `ProjectStoreCore.move`. */
export const MoveKnowledgeSchema = z.strictObject({ project: requiredText });

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

/**
 * Create a project: `slug` (a project with no folder) or `cwd` (a folder; the
 * slug defaults to its name) — at least one — and its description, which
 * every project has (`./labels.ts`). The cloud has no folders.
 */
/** A project's description: required, a label (`./labels.ts`). */
export const ProjectDescription = label(requiredText);

export const CreateProjectSchema = z
  .strictObject({
    slug: requiredText.optional(),
    cwd: requiredText.optional(),
    description: ProjectDescription,
  })
  .refine((v) => v.slug !== undefined || v.cwd !== undefined, 'send slug, cwd, or both');

// ---------------------------------------------------------------------------
// Operator profile (`./operator.ts`)
// ---------------------------------------------------------------------------

/** Optional profile text: `""` clears it. */
const clearableText = oneLine(z.string().trim());

/** A change to the operator profile. `name` and `timezone` can be replaced but not emptied. */
export const OperatorPatchSchema = z.strictObject({
  name: oneLine(requiredText).optional(),
  timezone: timeZone.optional(),
  shortName: clearableText.optional(),
  email: clearableText.refine((v) => v === '' || z.email().safeParse(v).success, 'not an email address').optional(),
  locale: clearableText.refine((v) => v === '' || isWellFormedLocale(v), 'not a locale tag (e.g. en-US)').optional(),
});
const _operatorPatchParity: MutuallyAssignable<z.infer<typeof OperatorPatchSchema>, OperatorPatch> = true;
void _operatorPatchParity;

/** Onboarding's profile step: a change that must name the operator. */
export const OnboardingProfileSchema = OperatorPatchSchema.extend({ name: oneLine(requiredText) });

// ---------------------------------------------------------------------------
// Agent identity, skills, onboarding
// ---------------------------------------------------------------------------

/** Text that must not be blank, kept exactly as sent (markdown is whitespace-sensitive). */
const nonBlankDocument = z.string().refine((v) => v.trim() !== '', 'required');

/** The agent identity (AGENTS.md), replaced whole. */
export const IdentitySchema = z.strictObject({ content: nonBlankDocument });

/** A new skill: its name and what it is for (the skills menu shows the description). */
export const CreateSkillSchema = z.strictObject({
  name: z
    .string()
    .trim()
    .superRefine((v, ctx) => {
      const problem = skillNameProblem(v);
      if (problem) ctx.addIssue({ code: 'custom', message: problem });
    }),
  description: oneLine(requiredText),
});

/** A skill's whole file, frontmatter included. */
export const UpdateSkillSchema = z.strictObject({
  content: z.string().superRefine((v, ctx) => {
    const problem = skillProblem(v);
    if (problem) ctx.addIssue({ code: 'custom', message: problem });
  }),
});

// ---------------------------------------------------------------------------
// Query strings (`checkRequest('query', …)`; value rules in `./queryValues.ts`)
// ---------------------------------------------------------------------------

/** Page sizes and windows the list routes serve: [min, max, default]. Above max is served as max. */
export const QUERY_BOUNDS = {
  analyticsDays: { min: 1, max: 90, fallback: 7 },
  activityDays: { min: 1, max: 30, fallback: 3 },
  activityLimit: { min: 1, max: 200, fallback: 50 },
  sessionSearchLimit: { min: 1, max: 50, fallback: 10 },
  knowledgeList: { min: 1, max: 200, fallback: 100 },
  routineRunsPerRoutine: { min: 1, max: 50, fallback: 10 },
  emailMessages: { min: 1, max: 50, fallback: 20 },
  emailAdminList: { min: 1, max: 100, fallback: 50 },
  fileTreeDepth: { min: 1, max: 6, fallback: 3 },
  searchResults: { min: 1, max: 1000 },
} as const;

/** `?days=` for the analytics summaries. */
export const AnalyticsDaysQuerySchema = z.strictObject({ days: boundedCount(QUERY_BOUNDS.analyticsDays) });

/** The activity feed: newest events, optionally one project's. */
export const ActivityQuerySchema = z.strictObject({
  limit: boundedCount(QUERY_BOUNDS.activityLimit),
  days: boundedCount(QUERY_BOUNDS.activityDays),
  project: text,
});

/** Search conversation history. */
export const SessionSearchQuerySchema = z.strictObject({
  q: requiredText,
  limit: boundedCount(QUERY_BOUNDS.sessionSearchLimit),
});

/** One page of a conversation: the messages before log index `before` (absent: the newest page). */
export const SessionMessagesQuerySchema = z.strictObject({
  before: wholeNumber.optional(),
});

/** Calendar events, optionally one month's (`YYYY-MM`) and one project's. */
export const CalendarListQuerySchema = z.strictObject({
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'must be a month, YYYY-MM')
    .optional(),
  project: text,
});

/** Contacts, optionally one project's and matching a search. */
export const ContactListQuerySchema = z.strictObject({ project: text, q: text });

/** A project's knowledge entries, newest first. */
export const KnowledgeListQuerySchema = z.strictObject({ limit: boundedCount(QUERY_BOUNDS.knowledgeList) });

/** The newest runs of every routine. */
export const RoutineRunsQuerySchema = z.strictObject({ limit: boundedCount(QUERY_BOUNDS.routineRunsPerRoutine) });

/** `?refresh=` / `?force=` — skip a cached answer. */
export const RefreshQuerySchema = z.strictObject({ refresh: flag });
export const ForceQuerySchema = z.strictObject({ force: flag });

/** Search the server logs. */
export const LogSearchQuerySchema = z.strictObject({
  level: z.enum(LOG_LEVELS).optional(),
  limit: optionalCount(QUERY_BOUNDS.searchResults),
  ctx: text,
  since: instant,
  q: text,
});

/** Search the tool-call record. */
export const ToolIoSearchQuerySchema = z.strictObject({
  outcome: z.enum(['success', 'error']).optional(),
  limit: optionalCount(QUERY_BOUNDS.searchResults),
  /** One record by its id (the model's tool-call id when known). */
  id: text,
  tool: text,
  caller: text,
  runId: text,
  sessionId: text,
  since: instant,
  before: instant,
  q: text,
});

/** The email list, optionally filtered by labels (comma-separated) and a project.
 *  `filtered` lists only the mail AgentMail's inbound filter held back (spam,
 *  blocked, unauthenticated — `@onestone/shared/emailFiltered`). */
export const EmailMessagesQuerySchema = z.strictObject({
  limit: boundedCount(QUERY_BOUNDS.emailMessages),
  labels: text,
  project: text,
  filtered: flag,
});

/** The AgentMail account's domains or inboxes. */
export const EmailAdminListQuerySchema = z.strictObject({ limit: boundedCount(QUERY_BOUNDS.emailAdminList) });

/** `?since=<seq>` — live events after this sequence number (all retained ones when absent). */
export const EventsQuerySchema = z.strictObject({ since: wholeNumber.optional() });

/** A folder to list (home when absent) and how deep. */
export const FileTreeQuerySchema = z.strictObject({ path: text, depth: boundedCount(QUERY_BOUNDS.fileTreeDepth) });

/** A folder to pick from (home when absent). */
export const FileBrowseQuerySchema = z.strictObject({ path: text });

/** One file by path. */
export const FilePathQuerySchema = z.strictObject({ path: requiredText });

/** The conversation whose project decides the file tree's root (none → no root). */
export const FilesCwdQuerySchema = z.strictObject({ conversationId: text });

/**
 * How an MCP client connects (`/mcp`): which conversation and project it acts
 * in, and — for sub-agents — `scope=readonly` with optional `categories`. A
 * misspelt scope or an unknown category is refused: read as "no scope" it
 * would hand a read-only worker every write tool.
 */
export const McpConnectQuerySchema = z
  .strictObject({
    conversation: text,
    project: text,
    /** The run this CLI session's calls belong to (`McpToolDeps.runId`) — set by `runClaudeCodeTurn`. */
    run: text,
    scope: z.literal('readonly').optional(),
    categories: z
      .string()
      .transform((v) => v.split(',').map((c) => c.trim()))
      .pipe(z.array(z.enum(TOOL_CATEGORIES)).min(1))
      .optional(),
    /** `external` — the session serves the connected external MCP servers' tools. */
    tools: z.literal('external').optional(),
    /** A routine's tool allowlist (`RoutineSpec.allowedTools`): the session lists and calls only these. */
    allow: z
      .string()
      .transform((v) => v.split(',').map((t) => t.trim()))
      .pipe(z.array(z.string().min(1)).min(1))
      .optional(),
  })
  // Categories narrow a read-only session only. A read-only session may serve
  // external tools: the external surface then lists only the ones on the
  // sub-agent's surface (trusted server + readOnlyHint, ADR-0006).
  .refine((q) => q.categories === undefined || q.scope === 'readonly', {
    message: 'categories only narrow a read-only (scope=readonly) session',
    path: ['categories'],
  })
  // A read-only session is a sub-agent's: it names its categories (the resolved
  // channelToolCategories.subagent), so no session falls back to a surface
  // nobody chose.
  .refine((q) => q.scope !== 'readonly' || q.categories !== undefined, {
    message: 'a read-only (scope=readonly) session must name its categories',
    path: ['categories'],
  });

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

/**
 * One chat turn (`POST /api/chat/stream`, both servers): text, attachments,
 * or both. `sessionId` is created when absent; `provider`/`model` override the
 * conversation's pin for this turn. Attachments are named by the ids
 * `POST /api/chat/upload` returned — the server finds the files itself.
 */
export const ChatTurnRequestSchema = z
  .strictObject({
    message: z.string().optional(),
    sessionId: requiredText.optional(),
    provider: requiredText.optional(),
    model: requiredText.optional(),
    /** Opt into the model's extended context window (e.g. 1M on Opus/Sonnet via API). */
    useExtendedContext: z.boolean().optional(),
    /** Restrict the tool surface by category; `['all']` forces the full allowed surface. */
    toolCategories: z.array(requiredText).optional(),
    attachmentIds: z.array(requiredText).max(MAX_ATTACHMENTS_PER_MESSAGE).optional(),
  })
  .refine(
    (v) => (v.message ?? '').trim() !== '' || (v.attachmentIds?.length ?? 0) > 0,
    'send a message, attachments, or both',
  );

/** `?sessionId=` — the conversation a turn-control request is about. */
export const SessionIdQuerySchema = z.strictObject({ sessionId: requiredText });

/** `?since=<seq>` — replay a running turn's buffered events after this sequence number (all when absent). */
export const TurnStreamQuerySchema = z.strictObject({
  since: z
    .string()
    .regex(/^-?\d+$/, 'must be a whole number')
    .transform((v) => Number(v))
    .optional(),
});

/** Answer the operator question a turn is waiting on (`ask_operator`). */
export const ChatAnswerSchema = z.strictObject({
  questionId: requiredText,
  action: z.enum(['accept', 'decline']),
  answer: z.string().optional(),
});

/** Switch a conversation's project — never cleared (General is the default); must exist (`checkRequest`). */
export const ActiveProjectSchema = z.strictObject({ project: requiredText });

/** Rename a conversation and/or mark it completed or active again — at least one. */
export const SessionPatchSchema = z
  .strictObject({
    title: oneLine(requiredText).optional(),
    status: z.enum(['active', 'completed']).optional(),
  })
  .refine((v) => v.title !== undefined || v.status !== undefined, 'send title, status, or both');

// ---------------------------------------------------------------------------
// Email (AgentMail)
// ---------------------------------------------------------------------------

/** Optional text that is dropped when blank (an empty cc is no cc). */
const optionalText = z
  .string()
  .trim()
  .optional()
  .transform((v) => (v === '' ? undefined : v));

/**
 * A file sent with a message: its name and base64 content. Both are required —
 * an attachment without them can't be sent, so it is refused rather than
 * dropped from a message the sender believes carries it.
 */
const EmailAttachmentSchema = z.strictObject({
  filename: requiredText,
  content: requiredText,
  contentType: requiredText.optional(),
});

/** A new message. `projects` must name existing projects (checked by `checkRequest`). */
export const SendEmailSchema = z.strictObject({
  to: requiredText,
  subject: requiredText,
  text: requiredText,
  html: optionalText,
  cc: optionalText,
  bcc: optionalText,
  replyTo: optionalText,
  labels: z.array(requiredText).optional(),
  projects: z.array(requiredText).optional(),
  attachments: z.array(EmailAttachmentSchema).optional(),
});

/** A reply to a message (to every recipient with `replyAll`). */
export const ReplyEmailSchema = z.strictObject({
  messageId: requiredText,
  text: requiredText,
  html: optionalText,
  cc: optionalText,
  bcc: optionalText,
  replyAll: z.boolean().optional(),
  attachments: z.array(EmailAttachmentSchema).optional(),
});

/** Forward a message, with an optional note. */
export const ForwardEmailSchema = z.strictObject({
  messageId: requiredText,
  to: requiredText,
  text: optionalText,
});

/** Labels to add and remove on a message — at least one. */
export const EmailLabelsSchema = z
  .strictObject({
    addLabels: z.array(requiredText).optional(),
    removeLabels: z.array(requiredText).optional(),
  })
  .refine(
    (v) => (v.addLabels?.length ?? 0) + (v.removeLabels?.length ?? 0) > 0,
    'send addLabels, removeLabels, or both',
  );

/** Projects to attach a message to and detach it from — at least one. */
export const EmailProjectsSchema = z
  .strictObject({
    add: z.array(requiredText).optional(),
    remove: z.array(requiredText).optional(),
  })
  .refine((v) => (v.add?.length ?? 0) + (v.remove?.length ?? 0) > 0, 'send add, remove, or both');

/** A custom sending domain; bounce/complaint feedback is on unless turned off. */
export const CreateEmailDomainSchema = z.strictObject({
  domain: requiredText.transform((v) => v.toLowerCase()),
  feedbackEnabled: z.boolean().optional(),
});

/** A new inbox — every field optional (AgentMail picks a random address). */
export const CreateInboxSchema = z.strictObject({
  username: optionalText,
  domain: optionalText,
  displayName: optionalText,
  clientId: optionalText,
});

/** An inbox's display name (the address itself can't change). */
export const UpdateInboxSchema = z.strictObject({ displayName: oneLine(requiredText) });

/** The inbox the dashboard and the agent operate as. */
export const ActiveInboxSchema = z.strictObject({ inbox: requiredText });

// ---------------------------------------------------------------------------
// Claude subscription (desktop)
// ---------------------------------------------------------------------------

/** The CLI runtime settings `PUT /api/claude/settings` owns (models are each channel's, `/api/config/channels/:channel`). */
export const ClaudeSettingsSchema = z.strictObject({
  authMode: z.enum(CLAUDE_AUTH_MODES).optional(),
  permissionMode: z.enum(CLAUDE_PERMISSION_MODES).optional(),
  /** The `claude` command to run; `""` goes back to the default. */
  command: z.string().trim().optional(),
});

/** The subscription token from `claude setup-token`. */
export const ClaudeTokenSchema = z.strictObject({ token: requiredText });

// ---------------------------------------------------------------------------
// Diagnostics (desktop) — the dashboard's own crash and jank reports
// ---------------------------------------------------------------------------

const nonNegativeMs = z.number().nonnegative();

/** Size limits of a renderer perf report — the dashboard truncates to them before sending. */
export const RENDERER_PERF_LIMITS = { scripts: 3, invoker: 200, invokerType: 40, source: 300, route: 200 } as const;

/** One script that ran inside a long animation frame (the API reports scripts over 5 ms). */
export const RendererPerfScriptSchema = z.strictObject({
  durationMs: nonNegativeMs,
  forcedLayoutMs: nonNegativeMs,
  /** How the script was entered, e.g. "Response.json.then", "BUTTON.onclick". */
  invoker: z.string().max(RENDERER_PERF_LIMITS.invoker),
  invokerType: z.string().max(RENDERER_PERF_LIMITS.invokerType),
  /** "file.js:functionName@charPosition" — the bundle file only, never a full URL. */
  source: z.string().max(RENDERER_PERF_LIMITS.source),
});

/** A long animation frame (W3C Long Animation Frames API) seen by the dashboard. */
export const RendererPerfSchema = z.strictObject({
  durationMs: z.number().positive(),
  blockingMs: nonNegativeMs,
  renderMs: nonNegativeMs,
  styleLayoutMs: nonNegativeMs,
  /** Frame start, ms since this page loaded — with pageId, identifies one frame. */
  startMs: nonNegativeMs,
  /** Random per page load: tells one document's reports from another's. */
  pageId: z.string().min(1).max(32),
  /** The route shown when the frame ended. */
  route: z.string().max(RENDERER_PERF_LIMITS.route).optional(),
  /** CSS-pixel viewport, "WIDTHxHEIGHT". */
  viewport: z.string().max(20).optional(),
  /** Frames over the threshold that the rate limit dropped since the last report. */
  dropped: z.number().int().nonnegative(),
  scripts: z.array(RendererPerfScriptSchema).max(RENDERER_PERF_LIMITS.scripts),
});
export type RendererPerfReport = z.infer<typeof RendererPerfSchema>;

/** Where a dashboard error surfaced — what caught it, not where it was thrown (rendererErrorReporter.ts). */
export const RendererErrorSourceSchema = z.enum([
  'boundary', // an <ErrorBoundary> componentDidCatch
  'uncaught', // React: no boundary caught it (root onUncaughtError)
  'caught', // React: a boundary caught it (root onCaughtError) — logged, not shown
  'recoverable', // React recovered on its own (root onRecoverableError)
  'window', // window 'error' event: outside React
  'unhandledrejection', // window 'unhandledrejection' event
  'chat-stream', // applying a streamed chat reply threw (useChatSend)
]);
export type RendererErrorSource = z.infer<typeof RendererErrorSourceSchema>;

/** A dashboard error: a React render crash (boundary or root callback) or an
 *  error thrown outside React (window error / unhandled rejection). */
export const RendererErrorSchema = z.strictObject({
  source: RendererErrorSourceSchema,
  message: requiredText,
  stack: z.string().optional(),
  componentStack: z.string().optional(),
  route: z.string().optional(),
  userAgent: z.string().optional(),
});

// ---------------------------------------------------------------------------
// External MCP servers (desktop)
// ---------------------------------------------------------------------------

/**
 * One external MCP server: a command (stdio) or a URL (http). Env and header
 * values are secrets the dashboard never receives back, so an edit names what
 * to keep: `env`/`headers` left out keeps the stored map; a name with an empty
 * value keeps its stored value; a name left out is removed; `{}` clears.
 */
export const ExternalMcpServerSchema = z
  .strictObject({
    transport: z.enum(['stdio', 'http']).optional(),
    command: requiredText.optional(),
    args: z.array(z.string()).optional(),
    env: z.record(z.string(), z.string()).optional(),
    url: z.url().optional(),
    headers: z.record(z.string(), z.string()).optional(),
    enabled: z.boolean().optional(),
    trusted: z.boolean().optional(),
  })
  .refine((v) => v.command !== undefined || v.url !== undefined, 'send command (stdio) or url (http)');

// ---------------------------------------------------------------------------
// Uploads (multipart forms, `checkRequest('form', …)`). Size limits are the
// routes' own (413 Payload Too Large); these check what was sent.
// ---------------------------------------------------------------------------

/** A file that has content. */
const nonEmptyFile = z.file().min(1, 'empty file');

/** One or more files under `files` (a single file arrives as one value, several as a list). */
export const ChatUploadFormSchema = z.strictObject({
  files: z.preprocess(
    (v) => (v === undefined ? [] : Array.isArray(v) ? v : [v]),
    z.array(nonEmptyFile).min(1, 'attach at least one file'),
  ),
});

/** A picture for the operator profile. */
export const AvatarFormSchema = z.strictObject({ avatar: nonEmptyFile });

/** Whisper model sizes the local transcriber can run, smallest (fastest) first. */
export const WHISPER_MODELS = ['tiny', 'base', 'small', 'medium', 'large'] as const;
export type WhisperModel = (typeof WHISPER_MODELS)[number];

/**
 * What the dashboard saw of the microphone during one recording. Sent with the
 * audio and logged with every transcription, so a failure carries its own
 * evidence (the phone's mic can't be inspected from the desktop).
 */
export const MicReportSchema = z.strictObject({
  /** The capture device's label (e.g. "Microphone", a headset's name). */
  label: z.string().max(200),
  /** `track.muted` when the recording stopped — true while the browser or OS withholds audio. */
  muted: z.boolean(),
  /** How many times the track went muted during the recording. */
  muteEvents: z.number().int().nonnegative(),
  readyState: z.string().max(20),
  /** `track.getSettings()` — what the browser actually applied. */
  settings: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
  /** Highest |sample| a level monitor saw (0 = digital silence); null when no monitor could run. */
  peak: z.number().min(0).nullable(),
  recorderMime: z.string().max(100),
  userAgent: z.string().max(400),
});
export type MicReport = z.infer<typeof MicReportSchema>;

/** A recording to transcribe; `mic` is a JSON `MicReport`. */
export const TranscribeFormSchema = z.strictObject({ audio: nonEmptyFile, mic: z.string().max(8000).optional() });

/** `?model=` — which Whisper model to run (the smallest when absent). */
export const TranscribeQuerySchema = z.strictObject({ model: z.enum(WHISPER_MODELS).optional() });

// ---------------------------------------------------------------------------
// Files and local models (desktop)
// ---------------------------------------------------------------------------

/** Write a text file under the home folder (the route checks the path stays there). */
export const FileWriteSchema = z.strictObject({ path: requiredText, content: z.string() });

/** Unload one Ollama model from memory, or every loaded model when `model` is absent. */
export const OllamaUnloadSchema = z.strictObject({ model: requiredText.optional() });

// ---------------------------------------------------------------------------
// Network (desktop)
// ---------------------------------------------------------------------------

/** A new API token; it is called "Untitled" when no name is given. */
export const CreateApiTokenSchema = z.strictObject({
  name: oneLine(z.string().trim())
    .optional()
    .transform((v) => v || 'Untitled'),
});

/** Turn the relay on, optionally at another relay (a `wss://` URL). */
export const RelayEnableSchema = z.strictObject({
  url: z
    .string()
    .trim()
    .refine((v) => /^wss?:\/\/[^\s]+$/.test(v), 'must be a ws:// or wss:// URL')
    .optional(),
});

/** Onboarding's identity step: the starter AGENTS.md's name and voice (defaults when absent). */
export const OnboardingIdentitySchema = z.strictObject({
  agentName: oneLine(z.string().trim()).optional(),
  style: oneLine(z.string().trim()).optional(),
});
